/**
 * Full state push and image data event handlers.
 * Caches fullState to IndexedDB for offline viewing.
 */

import { Platform } from "react-native";
import type { Socket } from "socket.io-client";
import type {
  ServerToClientEvents,
  ClientToServerEvents,
} from "@amzn/a4k-creator-studio-shared";
import { useStoryStore } from "../storyStore";
import { useComicStore } from "../comicStore";
import { useConfigStore } from "../configStore";
import { useSocketStore } from "../socketStore";
import { stopAccessRetry } from "./connectionHandlers";
import { ingestImage, isAwaited } from "../../lib/imageLoader";
import { setCachedImage } from "../../lib/imageCache";
import { rehydrateImages, applyImageToStores } from "./imageRehydration";
import { registerDb, put, getById } from "../../lib/storage";

const CACHE_DB = "fullstate";
const CACHE_STORE = "snapshots";
registerDb(CACHE_DB, [CACHE_STORE]);

/** Cache the fullState payload to IndexedDB (best-effort, strips imageBase64). */
function cacheFullState(data: any): void {
  try {
    const stripped = {
      id: "latest",
      story: data.story ? {
        ...data.story,
        characters: (data.story.characters || []).map(({ imageBase64, ...r }: any) => r),
        scenes: (data.story.scenes || []).map(({ imageBase64, ...r }: any) => r),
        beats: (data.story.beats || []).map(({ imageBase64, ...r }: any) => r),
      } : data.story,
      comic: data.comic ? {
        ...data.comic,
        pages: (data.comic.pages || []).map(({ imageBase64, ...r }: any) => r),
      } : null,
      config: data.config,
    };
    put(CACHE_DB, CACHE_STORE, stripped).catch(() => {});
  } catch { /* best-effort */ }
}

/** Load cached fullState from IndexedDB for offline use. */
export async function loadCachedFullState(): Promise<any | null> {
  try {
    const cached = await getById<any>(CACHE_DB, CACHE_STORE, "latest");
    return cached || null;
  } catch {
    return null;
  }
}

type TypedSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

export function registerFullStateHandlers(
  socket: TypedSocket,
  set: (partial: Partial<{ sessionId: string; fullStateReceived: boolean; storyLoading: boolean; sessionActive: boolean; accessBlocked: string | null }>) => void,
): void {
  socket.on("fullState", (data) => {
    console.log("[socketStore] Received fullState from server");
    stopAccessRetry();

    // Store server-assigned sessionId (may differ from hint if user->session mapping exists)
    if (data.sessionId) {
      set({ sessionId: data.sessionId });
      if (Platform.OS === 'web') {
        localStorage.setItem("story-builder-session-id", data.sessionId);
      }
    }

    // 1. Apply metadata immediately (fast — no images)
    useStoryStore.getState().replaceAll(data.story);
    if (data.comic) {
      useComicStore.getState().replaceAll(data.comic);
    }
    useConfigStore.getState().replaceAll(data.config);
    useConfigStore.getState().setModerationAvailable(data.moderationAvailable);
    useConfigStore.getState().setFeatureFlags(data.featureFlags);
    set({ fullStateReceived: true, storyLoading: false, sessionActive: true, accessBlocked: null });
    // No navigation here: a reconnect preserves the current screen. The explicit
    // resume paths (resumeStory / loadESSProject) call resetToBuilder themselves.

    // Resume a suspended voice session now that the server has re-established it
    // (fullState is the readiness signal). No-op unless voiceSuspended is set.
    useSocketStore.getState().resumeVoice();

    // Cache to IndexedDB for offline use
    cacheFullState(data);

    // Comic page images are omitted — the viewer loads those on demand.
    const allImageIds: string[] = [];
    for (const c of data.story?.characters || []) if (c.imageId) allImageIds.push(c.imageId);
    for (const s of data.story?.scenes || []) if (s.imageId) allImageIds.push(s.imageId);
    for (const b of data.story?.beats || []) if (b.imageId) allImageIds.push(b.imageId);
    rehydrateImages(socket, allImageIds);
  });

  socket.on("imageData", (data) => {
    const images = data.images || {};
    const entries = Object.entries(images);
    console.log(`[socketStore] Received ${entries.length} images from server`);
    for (const [imageId, base64] of entries) {
      // Route awaited page images through the loader; send story-entity blobs
      // straight to the store so they don't churn the page LRU.
      if (isAwaited(imageId)) {
        ingestImage(imageId, base64);
      } else {
        applyImageToStores(imageId, base64);
        setCachedImage(imageId, base64).catch(() => {});
      }
    }
  });
}
